
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, Clock } from "lucide-react";

// Updated to match article_mentions schema
interface ArticleMention {
  id: string;
  mentioned_profile_id: string;
  article_id: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  approved_at?: string;
  rejected_at?: string;
}

interface Article {
  id: string;
  title: string;
  content: string;
  author_name?: string;
}

interface MentionApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  mention: ArticleMention | null;
  article: Article | null;
  onApprove: (mentionId: string) => Promise<void>;
  onReject: (mentionId: string) => Promise<void>;
  loading?: boolean;
}

const MentionApprovalModal: React.FC<MentionApprovalModalProps> = ({
  isOpen,
  onClose,
  mention,
  article,
  onApprove,
  onReject,
  loading = false
}) => {
  if (!mention) return null;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const handleApprove = async () => {
    await onApprove(mention.id);
    onClose();
  };

  const handleReject = async () => {
    await onReject(mention.id);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Article Mention Request</DialogTitle>
          <DialogDescription>
            You have been mentioned in an article. Please review the content and decide whether to approve or decline this mention.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current Status */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Status:</span>
            <Badge className={`flex items-center gap-1 ${getStatusColor(mention.status)}`}>
              {getStatusIcon(mention.status)}
              {mention.status.charAt(0).toUpperCase() + mention.status.slice(1)}
            </Badge>
          </div>

          {/* Article Details or Fallback */}
          {article ? (
            <>
              {/* Article Title */}
              <div>
                <h3 className="font-semibold text-lg mb-2">{article.title}</h3>
                {article.author_name && (
                  <p className="text-sm text-gray-600 mb-3">
                    By {article.author_name}
                  </p>
                )}
              </div>

              {/* Article Content Preview */}
              <div className="border rounded-lg p-4 bg-gray-50 max-h-60 overflow-y-auto">
                <h4 className="font-medium mb-2">Article Preview:</h4>
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ 
                    __html: article.content.length > 500 
                      ? article.content.substring(0, 500) + '...' 
                      : article.content 
                  }}
                />
              </div>
            </>
          ) : (
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium mb-2">Article Information</h4>
              <p className="text-gray-600">
                The article details are not available at this time. This may be because the article has been removed or you don't have permission to view it.
              </p>
            </div>
          )}

          {/* Information Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> If you approve this mention, the article will appear on your profile's press tab. 
              If you reject it, the article will not be associated with your profile.
            </p>
          </div>
        </div>

        <DialogFooter>
          {mention.status === 'pending' && (
            <>
              <Button
                variant="outline"
                onClick={handleReject}
                disabled={loading}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Decline
              </Button>
              <Button
                onClick={handleApprove}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve
              </Button>
            </>
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MentionApprovalModal;
